import React from 'react';

interface DataTreatmentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DataTreatmentModal: React.FC<DataTreatmentModalProps> = ({ 
  isOpen, 
  onClose 
}) => {

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-center justify-center p-4">
      <div className={`max-w-4xl w-full max-h-[85vh] flex flex-col rounded-2xl ${
        'bg-white border-gray-200'
      } border shadow-2xl`}>
        
        {/* Header */}
        <div className={`flex-shrink-0 p-6 border-b ${
          'border-gray-200 bg-white'
        } rounded-t-2xl`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
                POLÍTICA DE TRATAMIENTO DE DATOS PERSONALES
              </h2>
              <p className="text-gray-600 text-sm">
                IGLESIA CRISTIANA CUADRANGULAR CENTRAL DE APARTADÓ<br/>
                Fecha de actualización: 19 de febrero de 2026
              </p>
            </div>
            <button
              onClick={onClose}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
              }`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className={`space-y-6 text-sm ${
            'text-gray-700'
          }`}>
            
            {/* 1. IDENTIFICACIÓN DEL RESPONSABLE */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                1. IDENTIFICACIÓN DEL RESPONSABLE
              </h3>
              <p className="leading-relaxed">
                <strong>Razón Social:</strong> IGLESIA CRISTIANA CUADRANGULAR CENTRAL DE APARTADÓ<br/>
                <strong>Domicilio:</strong> Apartadó, Antioquia, Colombia<br/><br/>
                La Iglesia actúa como responsable del Tratamiento de los datos personales 
                en cumplimiento de la Ley 1581 de 2012, el Decreto 1377 de 2013 y demás 
                normas concordantes.
              </p>
            </div>

            {/* 2. ALCANCE */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                2. ALCANCE
              </h3>
              <p className="leading-relaxed">
                La presente política aplica a todos los datos personales recolectados por la 
                Iglesia a través de encuestas, formularios físicos o digitales, registros de 
                asistencia, actividades religiosas, eventos, campañas sociales, misiones, 
                transmisiones audiovisuales y cualquier otro medio autorizado.
              </p>
            </div>

            {/* 3. FINALIDADES DEL TRATAMIENTO */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                3. FINALIDADES DEL TRATAMIENTO
              </h3>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Registro y control de miembros, asistentes y voluntarios.</li>
                <li>Gestión pastoral, acompañamiento espiritual y contacto ministerial.</li>
                <li>Organización de actividades religiosas, académicas y comunitarias.</li>
                <li>Envío de información institucional, invitaciones y comunicaciones.</li>
                <li>Elaboración de estadísticas internas y reportes administrativos.</li>
                <li>Cumplimiento de obligaciones legales y requerimientos de autoridades.</li>
              </ul>
            </div>

            {/* 4. DATOS SENSIBLES */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                4. DATOS SENSIBLES
              </h3>
              <p className="leading-relaxed">
                La Iglesia podrá recolectar datos sensibles como información relacionada 
                con creencias religiosas (dato especialmente protegido), estado de salud 
                o información de menores de edad. El titular no está obligado a autorizar el 
                tratamiento de datos sensibles. Su autorización será explícita y 
                diferenciada.
              </p>
            </div>

            {/* 5. TRATAMIENTO DE DATOS DE MENORES DE EDAD */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                5. TRATAMIENTO DE DATOS DE MENORES DE EDAD
              </h3>
              <p className="leading-relaxed">
                El tratamiento de datos personales de niños, niñas y adolescentes se 
                realizará respetando el interés superior del menor y sus derechos 
                fundamentales. La autorización deberá ser otorgada por el representante 
                legal del menor.
              </p>
            </div>

            {/* 6. DERECHOS DEL TITULAR */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                6. DERECHOS DEL TITULAR
              </h3>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Conocer, actualizar y rectificar sus datos personales.</li>
                <li>Solicitar prueba de la autorización otorgada.</li>
                <li>Ser informado sobre el uso dado a sus datos.</li>
                <li>Presentar quejas ante la Superintendencia de Industria y Comercio.</li>
                <li>Revocar la autorización y/o solicitar la supresión del dato cuando sea procedente.</li>
              </ul>
            </div>

            {/* 7. PROCEDIMIENTO PARA CONSULTAS Y RECLAMOS */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                7. PROCEDIMIENTO PARA CONSULTAS Y RECLAMOS
              </h3>
              <p className="leading-relaxed">
                Las consultas serán atendidas en un término máximo de diez (10) días 
                hábiles. Los reclamos serán resueltos dentro de los quince (15) días hábiles 
                siguientes a su recepción, conforme a los términos legales vigentes.
              </p>
            </div>

            {/* 8. MEDIDAS DE SEGURIDAD */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                8. MEDIDAS DE SEGURIDAD
              </h3>
              <p className="leading-relaxed">
                La Iglesia implementará medidas técnicas, humanas y administrativas 
                necesarias para proteger los datos personales contra pérdida, 
                adulteración, acceso no autorizado o fraude.
              </p>
            </div>

            {/* 9. REGISTRO NACIONAL DE BASES DE DATOS (RNBD) */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                9. REGISTRO NACIONAL DE BASES DE DATOS (RNBD)
              </h3>
              <p className="leading-relaxed">
                En caso de estar obligada legalmente, la Iglesia procederá a registrar sus 
                bases de datos ante el Registro Nacional de Bases de Datos administrado 
                por la Superintendencia de Industria y Comercio.
              </p>
            </div>

            {/* 10. VIGENCIA */}
            <div className={`p-4 rounded-lg border-l-4 border-blue-500 ${
              'bg-blue-50'
            }`}>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                10. VIGENCIA
              </h3>
              <p className={`text-sm ${
                'text-blue-800'
              }`}>
                La presente política rige a partir de su publicación y permanecerá vigente 
                mientras exista tratamiento de datos personales.
              </p>
            </div>
          </div>
          
          {/* Botón de aceptar */}
          <div className="flex justify-end pt-6">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105"
            >
              He leído y entiendo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataTreatmentModal;